import React, { useEffect, useState } from "react";
import {
    Sheet,
    SheetContent,
    SheetDescription,
    SheetHeader,
    SheetTitle,
} from "@/components/ui/sheet";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { useToast } from "@/components/ui/use-toast";
import { FormProvider, useForm } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { LoadingButton } from "@/components/ui/common/loading-button";
import { Separator } from "@/components/ui/separator";

import { ErrorType } from "@/types";
import { PrinterIcon } from "lucide-react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { Content } from "@tiptap/react";
import { MinimalTiptapEditor } from "@/components/ui/minimal-tiptap";
import { TooltipProvider } from "@/components/ui/tooltip";
import { EmailTemplate } from "@/types/system-setting/system-config";
import { useCreateEmailTemplatesMutation, useTestEmailMutation, useUpdateEmailTemplatesMutation } from "@/services/systemConfigApi";
import { Info } from "lucide-react";

interface ReminderTemplateFormProps {
    isOpen: boolean;
    setOpen: React.Dispatch<React.SetStateAction<boolean>>;
    action: string;
    setAction: React.Dispatch<React.SetStateAction<"add" | "edit">>;
    refetch: any;
    editModeData: any | undefined;
}

const ReminderTemplatesForm = ({
    isOpen,
    setOpen,
    action,
    setAction,
    editModeData,
    refetch,
}: ReminderTemplateFormProps) => {
    const { toast } = useToast();

    // text editor
    const [textEditorValue, setTextEditoralue] = useState<Content>("");
    const [createEmailTemplate, { error: createError, status: createStatus }] = useCreateEmailTemplatesMutation();
    const [updateEmailTemplate, { isLoading, error, status }] = useUpdateEmailTemplatesMutation();
    const [testEmail, { isLoading: testEmailLoading, error: testEmailError, status: testEmailStatus }] = useTestEmailMutation();
    const initialValues = {
        subject: "",
        name: "",
        key: "",
        body: "",
        place_holder: '',
        status: true //issue here
    };

    const form = useForm<EmailTemplate>({
        mode: "all",
        defaultValues: initialValues,
    });

    const {
        control,
        watch,
        register,
        handleSubmit,
        clearErrors,
        getValues,
        reset,
        setValue,
        formState: { isSubmitting, errors },
    } = form;
    const watcher = watch();

    useEffect(() => {
        if (action == "edit") {
            const payload = { ...editModeData, status: true };
            setTextEditoralue(payload.body as Content);
            reset(payload as EmailTemplate);
        } else {
            reset(initialValues);
        }
    }, [action]);

    const handleTestEmail = async () => {
        const payload = {
            name: watcher.name,
            subject: watcher.subject,
            body: textEditorValue as string
        }
        const resp = await testEmail(payload).unwrap();
        if (resp) {
            toast({
                variant: "success",
                title: "Email sent successfully",
            });
        }
    };

    const onSubmit = async (payload: EmailTemplate) => {
        if (payload.body.length == 0) {
            toast({
                variant: "destructive",
                title: "Email body is required",
            });
            return;
        }
        try {



            if (action === "add") {
                const resp = await createEmailTemplate(payload).unwrap();
                if (resp) {
                    toast({
                        variant: "success",
                        title: "Email template created successfully",
                    });

                }
            } else if (action === "edit") {
                const resp = await updateEmailTemplate(payload).unwrap();
                if (resp) {
                    toast({
                        variant: "success",
                        title: "Email template updated Successfully",
                    });
                }
            }

            await refetch();
            handleClose();

        } catch (error) {
            console.error("Error", { error });
            if (error && typeof error === "object" && "data" in error) {
                const typedError = error as ErrorType;
                toast({
                    variant: "destructive",
                    title: "Error in Submission",
                    description: `${typedError.data?.detail || typedError.data?.message}`,
                });
                return false;
            } else {
                toast({
                    variant: "destructive",
                    title: "Error in Submission",
                    description: `Something Went Wrong.`,
                });
                return false;
            }
        }
    };

    const onError = () => {
        toast({
            variant: "destructive",
            description: "Please fill all the mandatory fields",
        });
    };

    const handleClose = () => {
        clearErrors();
        setAction("add");
        reset(
            initialValues,
            {
                keepIsSubmitted: false, // Reset the form's submission state
                keepSubmitCount: false, // Reset the count of submissions
                keepDirtyValues: false, // Clear any dirty values (set to false to reset all fields)
                keepDefaultValues: true, // Keep default values as provided by initialValue
            }
        );
        setTextEditoralue("")

        setOpen(false);
    };
    const placeholderList = [
        "gym_name",
        "gym_email",
        "member_name",
        "app_link_ios",
        "app_link_android",
        "total",
        "discount",
        "tax_amount",
        "created_at"
    ]

    const copyToClipboard = async (text: string) => {
        const isSecure = window.isSecureContext;

        if (isSecure && navigator.clipboard?.writeText) {
            try {
                await navigator.clipboard.writeText(text);
                console.log("✅ Used Clipboard API");
                return true;
            } catch (err) {
                console.warn("❌ Clipboard API failed, using fallback", err);
            }
        }

        // fallback
        try {
            const textarea = document.createElement("textarea");
            textarea.value = text;
            textarea.style.position = "fixed"; // avoid scroll jump
            textarea.style.opacity = "0";
            document.body.appendChild(textarea);
            textarea.focus();
            textarea.select();
            const success = document.execCommand("copy");
            document.body.removeChild(textarea);
            console.log("✅ Used fallback (execCommand)");
            return success;
        } catch (e) {
            console.error("❌ Both Clipboard API and fallback failed", e);
            return false;
        }
    };

    console.log({ watcher, errors, action });
    return (
        <Sheet open={isOpen} onOpenChange={handleClose}>
            <SheetContent
                hideCloseButton
                className="bg-formbg w-full lg:!max-w-[1000px] p-0 custom-scrollbar h-full "
            >
                <FormProvider {...form}>
                    <form
                        key={action}
                        noValidate
                        className="pb-4 "
                        onSubmit={handleSubmit(onSubmit, onError)}
                    >
                        <SheetHeader className="px-6 sticky top-0 z-40 py-3 bg-white">
                            <SheetTitle>
                                <div className="flex justify-between items-center flex-col md:flex-row gap-5  bg-white">
                                    <div>
                                        <p className="font-semibold">{action == "add" ? "Add" : "Edit"} Invoice Reminder</p>

                                    </div>

                                    <div className="flex justify-center space-x-[20px]">
                                        <Button
                                            type="button"
                                            className="w-[100px] text-center flex items-center gap-2 border-primary"
                                            variant={"outline"}
                                            disabled={isSubmitting}
                                            onClick={handleClose}
                                        >
                                            <i className="fa fa-xmark "></i>
                                            Cancel
                                        </Button>
                                        <Button
                                            type="button"
                                            className="w-fit text-center flex items-center gap-2 border-primary"
                                            variant={"outline"}
                                            disabled={isSubmitting || testEmailLoading}
                                            onClick={handleTestEmail}
                                        >
                                            <i className="fa-regular fa-envelope "></i>
                                            Test Email
                                        </Button>
                                        <LoadingButton
                                            loading={isSubmitting}
                                            disabled={isSubmitting}
                                            type="submit"
                                            className="w-[110px] cursor-pointer bg-primary hover:bg-primary flex items-center gap-1"
                                        >
                                            {!isSubmitting &&
                                                <i className="fa-regular fa-floppy-disk text-base px-1"></i>
                                            }
                                            {action == "add" ? "Save" : "Update"}
                                        </LoadingButton>
                                    </div>
                                </div>
                            </SheetTitle>
                        </SheetHeader>
                        <Separator className=" h-[1px] rounded-full     " />

                        <SheetDescription className=" h-full  p-6  ">
                            <Card className="w-full ">
                                <div className="space-y-2 p-4">
                                    <div className="flex gap-2 items-start bg-yellow-50 p-4 rounded-sm text-yellow-800">
                                        <Info className="h-5 w-5 mt-1" />
                                        <div className="space-y-2">
                                            <p className="font-medium text-sm">Available placeholders for your message:</p>
                                            <div className="flex flex-wrap gap-2">
                                                {placeholderList?.map((placeholder) => (
                                                    <span
                                                        key={placeholder}
                                                        className="bg-yellow-100 px-2 py-1 rounded-md text-xs font-medium hover:bg-yellow-200 transition-colors"
                                                        title="Click to copy"
                                                        onClick={async () => {
                                                            const success = await copyToClipboard(`{{${placeholder}}}`);
                                                            toast({
                                                                variant: success ? "success" : "destructive",
                                                                title: success ? "Copied" : "Copy failed",
                                                                duration: 2000,
                                                            });
                                                        }}
                                                        style={{ cursor: 'pointer' }}
                                                    >
                                                        {placeholder}
                                                    </span>
                                                ))}
                                            </div>
                                            <p className="text-xs text-yellow-700 mt-2">Click on a placeholder to copy to clipboard</p>
                                        </div>
                                    </div>
                                </div>

                                <CardContent className="space-y-6 p-4">
                                    <div className="space-y-4">
                                        <div className="grid gap-4 sm:grid-cols-2">
                                            <div className="space-y-2">
                                                <label
                                                    htmlFor="templateName"
                                                    className="text-sm text-zinc-500"
                                                >
                                                    Template Name
                                                </label>
                                                <Input
                                                    id="templateName"
                                                    placeholder="Enter Template Name"
                                                    className="h-10 placeholder:text-zinc-400"
                                                    value={watcher.name}

                                                    {...register("name", {
                                                        required: "Required",
                                                        maxLength: {
                                                            value: 50,
                                                            message: "Maximum 50 characters allowed"
                                                        }
                                                    })}
                                                />
                                                {errors.name && (
                                                    <p className="text-destructive text-xs mt-[5px]">{errors.name.message}</p>
                                                )}
                                            </div>
                                            <div className="space-y-2">
                                                <label
                                                    htmlFor="emailSubject"
                                                    className="text-sm text-zinc-500"
                                                >
                                                    Email Subject
                                                </label>
                                                <Input
                                                    id="emailSubject"
                                                    type="text"
                                                    placeholder="Enter Subject"
                                                    value={watcher.subject}
                                                    className="h-10 placeholder:text-zinc-400"
                                                    {...register("subject", {
                                                        required: "Required",
                                                        maxLength: {
                                                            value: 150,
                                                            message: "Maximum 150 characters allowed"
                                                        },
                                                        validate: (value) => {
                                                            const trimmed = value.trim();
                                                            const isEmail = /^[\w.-]+@[\w.-]+\.\w{2,}$/i.test(trimmed);
                                                            return isEmail ? "Subject must not be an email address" : true;
                                                        },
                                                    })}
                                                />
                                                {errors.subject && (
                                                    <p className="text-destructive text-xs mt-[5px]">{errors.subject.message}</p>
                                                )}
                                            </div>
                                        </div>
                                        <div className="space-y-2">
                                            <label
                                                htmlFor="emailBody"
                                                className="text-sm text-zinc-500"
                                            >
                                                Email Body
                                            </label>
                                            <div className="space-y-2">
                                                <TooltipProvider>
                                                    <MinimalTiptapEditor
                                                        value={textEditorValue}
                                                        content={textEditorValue}
                                                        onChange={(value) => {
                                                            setTextEditoralue(value);
                                                            setValue("body", value as string);
                                                        }}
                                                        className="w-full"
                                                        editorContentClassName="p-5"
                                                        output="html"
                                                        placeholder="Type your description here..."
                                                        autofocus={true}
                                                        editable={true}
                                                        editorClassName="focus:outline-none"
                                                    />

                                                </TooltipProvider>
                                            </div>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        </SheetDescription>
                    </form>
                </FormProvider>
            </SheetContent>
        </Sheet>
    );
};

export default ReminderTemplatesForm;
